import type {
  MedicalAidPerson,
  PatientMedicalAidOnboardRequest,
  PersonContactWithAddressEmergencyAccess,
} from '@quromedical/models';
import { Alert, Snackbar } from 'design-system';
import { router } from 'expo-router';
import { type ErrorMessageType, getErrorMessage } from 'helpers';
import { organizationSchemeCodeFetcher } from 'integration';
import { CoverageApi, PatientOnboardApi } from 'integration/aggregate';
import type { OnboardStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';

import type { JointFields } from '../internal';
import { type OnSearch, SchemeSearchForm, type SearchResult } from './forms';
import type {
  NextOfKinStepData,
  PatientStepData,
  Step,
  SubmittingStateData,
} from './internal';
import { NavigationToggle } from './navigation';
import { NextOfKinStep, PatientStep } from './steps';

// TODO: maybe use XState
type FormStep =
  | Step<'search'>
  | Step<'searching'>
  | Step<'no-search-results'>
  | Step<'patient', PatientStepData>
  | Step<'next-of-kin', NextOfKinStepData>
  | Step<'submitting', SubmittingStateData>
  | Step<'done'>
  | Step<'error', ErrorMessageType>;

const api = new PatientOnboardApi();

// this function will be cleaned up when the state management is aligned to the data needed here
const mapStateToOnboardData = (
  stateData: SubmittingStateData,
): PatientMedicalAidOnboardRequest => ({
  patient: {
    identifierValue: stateData.patient.general.identifierValue as string,
    contact: stateData.patient
      .contact as PersonContactWithAddressEmergencyAccess,
  },
  coverage: {
    schemeCode: stateData.patient.medicalAidMembership?.medicalAid
      ?.schemeCode as string,
    dependantCode: stateData.patient.medicalAidMembership
      ?.dependantCode as string,
    subscriberId: stateData.patient.medicalAidMembership?.medicalAid
      ?.membershipNumber as string,
  },
  nextOfKin: {
    contact: {
      ...stateData.nextOfKin,
    },
    general: {
      ...stateData.nextOfKin,
    },
  },
});

const coverageApi = new CoverageApi();

export const OnboardMedicalAidScreen: React.FC<
  OnboardStackScreenProps<'MedicalAid'>
> = () => {
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [formStep, setFormStep] = useState<FormStep>({
    key: 'search',
  });

  const handleSearchStart = useCallback(() => {
    setFormStep({
      key: 'searching',
    });
  }, []);

  const handleSearchResult = useCallback((result: SearchResult) => {
    setFormStep({
      key: 'patient',
      data: result,
    });
  }, []);

  const handlePatientSubmit = useCallback(
    (patient: MedicalAidPerson) => {
      if (formStep.key !== 'patient') {
        return;
      }

      setFormStep({
        key: 'next-of-kin',
        data: {
          patient,
          search: formStep.data,
        },
      });
    },
    [formStep],
  );

  const handleNextOfKinSubmit = useCallback(
    async (nextOfKin: JointFields) => {
      if (formStep.key !== 'next-of-kin') {
        return;
      }

      const nextState: FormStep = {
        key: 'submitting',
        data: {
          ...formStep.data,
          nextOfKin,
        },
      };

      setFormStep(nextState);

      // this entire state management here is a but of a mess due to the previous model version,
      // will fix using a better state management solution
      try {
        const onboardData: PatientMedicalAidOnboardRequest =
          mapStateToOnboardData(nextState.data);

        // TODO: store the data in the correct types in the sub-steps so we don't have to do this
        const result = await api.onboardMedicalAid(onboardData);

        // navigate to the page for the created patient on completion

        const id = result.patientId;
        if (!id) return;

        router.push(`/patient/${id}/profile`);

        setFormStep({
          key: 'done',
        });
      } catch (err) {
        setFormStep({
          key: 'error',
          data: getErrorMessage(err),
        });
        setShowAlert(true);
      }
    },
    [formStep],
  );

  const hasPatientResults =
    formStep.key === 'patient' && formStep.data.result.length;
  const onSearch = useCallback<OnSearch>(async (search) => {
    try {
      return await coverageApi.familyCheck(search);
    } catch (error) {
      setFormStep({
        key: 'error',
        data: getErrorMessage(error),
      });
      setShowAlert(true);
      return [];
    }
  }, []);

  const onAlertClose = useCallback(() => {
    setShowAlert(false);
  }, []);

  const showError = formStep.key === 'error';
  const errorMessage = showError
    ? formStep.data
    : strings.MedicalAidSearchError;

  return (
    <ScrollView>
      <NavigationToggle active="MedicalAid" />

      <SchemeSearchForm
        onResult={handleSearchResult}
        onStart={handleSearchStart}
        onSearch={onSearch}
        schemeFetcher={organizationSchemeCodeFetcher}
      />

      {hasPatientResults ? (
        <PatientStep
          key={
            formStep.data.search.id + formStep.data.search.schemeDetails.value
          }
          search={formStep.data.search}
          results={formStep.data.result}
          onSubmit={handlePatientSubmit}
        />
      ) : null}

      {formStep.key === 'next-of-kin' ? (
        <NextOfKinStep
          onSubmit={handleNextOfKinSubmit}
          patient={formStep.data.patient}
          results={formStep.data.search.result}
        />
      ) : null}

      <Snackbar isOpen={showAlert} onClose={onAlertClose}>
        <Alert
          actionIcon="close"
          backgroundColor="status-critical"
          onAction={onAlertClose}
          title={strings.AlertError}
          body={errorMessage as string}
          icon="error"
        />
      </Snackbar>
    </ScrollView>
  );
};
